import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import { onServerError, onModuleBlocked, fetchAppStatus } from '@clarix/api-client';
import { hasInternet, subscribeConnectivity } from '@clarix/utils';
import { env } from '@/config/env';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AppBootSplash } from './AppBootSplash';
import { AppUnavailable } from './AppUnavailable';
import { ModuleUnavailable } from './ModuleUnavailable';
import { MaintenancePage } from './MaintenancePage';
import { setAppOutage } from './appOutageStore';
import { markShellBooted, useShellBooted } from './shellBootedStore';
import { setModuleUnavailable, useModuleUnavailable } from './moduleUnavailableStore';
import { lazyWithRetry } from './lazyWithRetry';
import { isShellRecorderCall } from './quick-meeting/api';
import { useBootTrace } from '@/gateway/devtools/bootTrace';

/**
 * Records that a remote has rendered, so later chunk loads swap the cold-boot
 * splash for the app-level skeleton. Inside the Suspense boundary on purpose:
 * it cannot mount until the chunk resolves, so the flag can never flip while a
 * load is still in flight.
 */
function MarkShellBooted() {
  useBootTrace('RemoteResolved');
  useEffect(() => markShellBooted(), []);
  return null;
}

interface AppRouteProps {
  appName: string;
  /** Registry slug — the key the gateway's per-app status endpoint is keyed by. */
  appSlug: string;
  loader: () => Promise<{ default: ComponentType }>;
}

/**
 * What a confirmed backend failure turned out to be.
 *   'app'      — THIS app's service is down; the rest of the platform is fine.
 *   'platform' — global maintenance, or the gateway itself is unreachable.
 */
type Outage = 'app' | 'platform';

/**
 * Status-probe retry. After the laptop wakes from sleep the wifi takes a few
 * seconds to re-associate, and every request fired in that window — including
 * a single status probe — fails with no response. One failed probe is therefore
 * not evidence of anything; spread a few over a few seconds first.
 */
const PROBE_ATTEMPTS = 3;
const PROBE_RETRY_DELAY_MS = 1_500;

/** How often to re-probe while the platform maintenance page is up. */
const RECOVERY_POLL_MS = 5_000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A wall raised but not yet tied to a history entry; the next render ties it to the one on screen. */
const UNBOUND = '\0unbound';

/**
 * Decide whether a backend error is a real outage, and which kind. Resolves
 * `null` for "not an outage" — a transient blip, or the user's own connection
 * (no internet: the OfflineBanner owns that, never a maintenance page).
 */
async function verifyOutage(appSlug: string): Promise<Outage | null> {
  for (let attempt = 1; attempt <= PROBE_ATTEMPTS; attempt += 1) {
    try {
      const status = await fetchAppStatus(env.VITE_API_BASE_URL, appSlug);
      if (status.available) return null; // healthy → the error was transient
      // Global maintenance walls off everything, so it earns the full-screen
      // page. An app-only outage stays inside the chrome so the user can switch
      // to an app that works.
      return status.maintenance ? 'platform' : 'app';
    } catch {
      if (attempt < PROBE_ATTEMPTS) await delay(PROBE_RETRY_DELAY_MS);
    }
  }
  // The gateway never answered. Only a genuine platform outage if the internet
  // itself works — `navigator.onLine` can't tell (it stays true on a wifi with
  // no uplink, and right after wake), so ask a real reachability probe.
  return (await hasInternet({ force: true })) ? 'platform' : null;
}

/**
 * Mounts a registered app: retried lazy import, Suspense, an error boundary, and outage and
 * plan walls scoped to THIS app. The retry key remounts the subtree on "Try again" so a fresh
 * React.lazy re-runs the import; React otherwise replays the cached rejection.
 */
export function AppRoute({ appName, appSlug, loader }: AppRouteProps) {
  const [retryKey, setRetryKey] = useState(0);
  // Set only once a backend error has been CONFIRMED to be a real outage by the
  // status probe. A transient one-off error never sets this.
  const [outage, setOutage] = useState<Outage | null>(null);
  // Which module (if any) this workspace is not entitled to. Published by the
  // shared store so it survives the remount below and is readable by chrome.
  const blockedModule = useModuleUnavailable();
  const { key: locationKey } = useLocation();
  // The history entry the wall went up on. The listener can't know it (a page blocked on mount
  // emits before this component's effects see the new route), so it records UNBOUND.
  const [blockedKey, setBlockedKey] = useState<string | null>(null);
  // The wall belongs to the visit that was refused: any navigation, even to the same path, takes
  // it down (during render, so the remote mounts in the same pass) and the page re-evaluates.
  if (blockedKey === UNBOUND) setBlockedKey(locationKey);
  else if (blockedKey !== null && blockedKey !== locationKey) setBlockedKey(null);
  // False only on a cold boot; true once any remote has mounted this document.
  const booted = useShellBooted();
  // While one health probe is deciding whether an error is a real outage, later
  // errors only re-log rather than each spawning their own probe.
  const probingRef = useRef(false);
  useBootTrace(`AppRoute:${appSlug}`, { booted, outage, retryKey });

  // Subscribe to backend-outage events while this app is mounted. Only the
  // active app is mounted and making its own calls, so an outage is naturally
  // attributed to it; other apps and auth (excluded in api-client) are
  // unaffected. The failing request is logged so an intermittent first-load
  // 5xx can be attributed from the console (the UI shows only code/status).
  useEffect(
    () =>
      onServerError((event) => {
        console.error(
          `[${appName}] backend error: ${event.code} (${event.status}) on ${event.url}`,
        );
        // One failed request is not an outage: keep the user where they are, then verify.
        // The probe is THIS app's status (/system/status/<slug>), never the platform's, which
        // is proxied to projects-api and would make one service's outage wall every app.
        if (probingRef.current) return; // a health check is already deciding
        probingRef.current = true;
        verifyOutage(appSlug)
          .then((verdict) => {
            if (verdict) setOutage(verdict);
          })
          .finally(() => {
            probingRef.current = false;
          });
      }),
    [appName, appSlug],
  );

  // Per-tenant module entitlement. Unlike an outage this needs NO health probe
  // and no retry: a 403 MODULE_NOT_ENABLED is a definitive answer about the
  // tenant's plan, not a transient failure. Cleared on unmount so switching
  // apps never leaves a stale wall up.
  useEffect(() => {
    const off = onModuleBlocked((event) => {
      // Shell chrome calls other apps' APIs from every app (calendar's next-meeting chip),
      // so only a denial from THIS app's own API, made by the page, may wall it.
      if (event.app.trim().toUpperCase() !== appSlug.toUpperCase()) return;
      if (isShellRecorderCall(event.url)) return;
      console.warn(
        `[${appName}] module not enabled: ${event.module} (${event.app}) on ${event.url}`,
      );
      setBlockedKey(UNBOUND);
      setModuleUnavailable({ module: event.module, app: event.app });
    });
    return () => {
      off();
      setModuleUnavailable(null);
    };
  }, [appName, appSlug]);

  // Tell the shell chrome which app is down, so AppShellLayout can render this
  // app's FULL rail (disabled) instead of collapsing to the ungated items — the
  // RBAC matrix can't hydrate while the app's own api is unreachable. Cleared on
  // recovery and on unmount so switching apps never leaves a stale outage.
  useEffect(() => {
    setAppOutage(outage === 'app' ? appSlug : null);
    return () => setAppOutage(null);
  }, [outage, appSlug]);

  const retry = useCallback(() => {
    setOutage(null);
    setRetryKey((k) => k + 1); // remount → refetch the app's queries
  }, []);

  // Platform page up → heal on its own. MaintenancePage does not self-poll when
  // it is given an onRetry, and AppUnavailable (the 'app' case) polls itself,
  // so without this a false positive — e.g. the flaky seconds after waking
  // from sleep — stayed on screen until the user clicked "Try Again".
  const platformDown = outage === 'platform';
  useEffect(() => {
    if (!platformDown) return undefined;
    let active = true;
    let checking = false;
    const check = async () => {
      if (checking || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const status = await fetchAppStatus(env.VITE_API_BASE_URL, appSlug);
        if (active && status.available) retry();
      } catch {
        // Still unreachable. If that is the user's own connection, the
        // OfflineBanner says so; keep polling either way.
      } finally {
        checking = false;
      }
    };
    const id = window.setInterval(() => void check(), RECOVERY_POLL_MS);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    const offConnectivity = subscribeConnectivity(onVisible);
    return () => {
      active = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
      offConnectivity();
    };
  }, [platformDown, appSlug, retry]);

  // Intentionally (re)create the lazy component inside render: bumping retryKey
  // must produce a fresh React.lazy so "Try again" re-runs the import instead of
  // replaying React's cached rejected promise. See the retry-key note above.
  const LazyApp = useMemo(
    () =>
      lazyWithRetry(loader, {
        onError: (err, attempt) => {
          console.warn(`[${appName}] chunk load failed (attempt ${attempt})`, err);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retryKey, loader, appName],
  );

  if (blockedModule && blockedKey === locationKey) {
    // Checked before the outage branches: "your plan does not include this" is
    // a definitive answer, whereas an outage is a guess awaiting a probe.
    return (
      <ModuleUnavailable
        appName={appName}
        moduleCode={blockedModule.module}
        appHref={`/${appSlug}`}
      />
    );
  }

  if (outage === 'app') {
    // Scoped panel INSIDE the chrome — the rail and app switcher stay usable so
    // the user can move to an app whose backend is healthy.
    return <AppUnavailable appName={appName} appSlug={appSlug} onRetry={retry} />;
  }

  if (outage === 'platform') {
    // Full maintenance page. onRetry is caller-owned here, so the page remounts
    // this app on "Try Again" instead of polling the global status + reloading.
    return <MaintenancePage onRetry={retry} />;
  }

  return (
    <AppErrorBoundary key={retryKey} appName={appName} onRetry={retry}>
      {/* Both tiers wear the destination app's own mark (its favicon) — only
          the weight differs. Cold boot fills the viewport; once the chrome is
          painted a remote hand-off is one panel filling in, so it renders
          compact rather than restaging the whole product. */}
      <Suspense fallback={<AppBootSplash compact={booted} />}>
        <MarkShellBooted />
        {/* eslint-disable-next-line react-hooks/static-components -- retry recreation is deliberate (see above) */}
        <LazyApp />
      </Suspense>
    </AppErrorBoundary>
  );
}
